import React, { RefObject } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconFileTypeXls } from '@tabler/icons-react';

import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { IconOngoingCalls } from '@shared/assets/images/svgs';
import { CallsFilter } from '@shared/components/callsFilter/callsFilter';
import { CustomTooltip } from '@shared/components/customTooltip';
import { CampaignDto } from '@shared/services/apiService/apiService';

interface OngoingCallsToolbarProps {
  headerRef: RefObject<HTMLDivElement | null>;
  campaignData: CampaignDto;
  callsCount: number;
  isExportReportAvailable: boolean;
  onOpenReportDialog: () => void;
  formMethods: UseFormReturn<CallsFilterFormValue>;
  classes: { header_title: string };
  isExtendedSearch: boolean;
  onExtendedSearchChange: (value: boolean) => void;
}

export const OngoingCallsToolbar: React.FC<OngoingCallsToolbarProps> = ({
  headerRef,
  campaignData,
  callsCount,
  isExportReportAvailable,
  onOpenReportDialog,
  formMethods,
  classes,
  isExtendedSearch,
  onExtendedSearchChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box ref={headerRef}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          'svg path': {
            stroke: theme.palette.getContrastText(theme.palette.background.default),
          },
        }}>
        <IconOngoingCalls />
        <Typography fontSize="18px" fontWeight={600}>
          {t('campaignPage.calls')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
        <Box
          className={classes.header_title}
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}>
          <Typography fontSize="18px" sx={{ minWidth: '110px' }}>
            {t('campaignPage.editCampaign.calls')}
          </Typography>
          <Typography
            fontSize="18px"
            color={theme.palette.text.secondary}
            sx={{ minWidth: '42px' }}>
            ({callsCount})
          </Typography>
        </Box>
        <CustomTooltip
          title={
            isExportReportAvailable ? null : t('campaignPage.ongoingCallsTable.report.unavailable')
          }
          placement="top"
          arrow>
          <span>
            <Button onClick={onOpenReportDialog} disabled={!isExportReportAvailable}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  whiteSpace: 'nowrap',
                  gap: 1,
                }}>
                {t('campaignPage.ongoingCallsTable.report.title')}
                <IconFileTypeXls />
              </Box>
            </Button>
          </span>
        </CustomTooltip>
      </Box>
      <CallsFilter
        campaignData={campaignData}
        formMethods={formMethods}
        isExtendedSearch={isExtendedSearch}
        onExtendedSearchChange={onExtendedSearchChange}
      />
    </Box>
  );
};
